import React, { useState, useRef, useEffect } from "react";
import { getCountries, getCountryCallingCode } from "react-phone-number-input/input";
import en from "react-phone-number-input/locale/en.json";
import zh from "react-phone-number-input/locale/zh.json";

// Popular countries to show at the top (can be customized)
const DEFAULT_TOP_COUNTRIES = ["CN", "HK", "TW", "US", "GB"];

// Map of countries to their flag emoji
const countryToFlag = (countryCode) => {
  if (!countryCode) return "";
  // Convert country code to regional indicator symbols
  // These will be displayed as a flag on supported systems
  const codePoints = countryCode
    .toUpperCase()
    .split("")
    .map((char) => 127397 + char.charCodeAt(0));
  return String.fromCodePoint(...codePoints);
};

/**
 * Custom Dropdown Country Selector Component with limited height
 *
 * This component replaces the native select element with a custom dropdown
 * that can have a constrained height.
 */
const CountrySelector = ({
  value = "+852",
  onChange,
  isChineseLanguage = false,
  topCountries = DEFAULT_TOP_COUNTRIES,
  className = "w-24 px-2 py-2 border border-gray-300 rounded-l-md bg-gray-50",
  showCountryName = false,
  id = "countryCode",
  maxDropdownHeight = "300px", // Maximum height of dropdown
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedValue, setSelectedValue] = useState(value);
  const dropdownRef = useRef<HTMLDivElement | null>(null);

  // Get user's language preference for country names
  const countryLocale = isChineseLanguage ? zh : en;

  // Get all available countries
  const countries = getCountries();

  // Sort countries to show top countries first, then alphabetically by name
  const sortedCountries = [...countries].sort((a, b) => {
    const aIsTop = topCountries.includes(a);
    const bIsTop = topCountries.includes(b);

    if (aIsTop && !bIsTop) return -1;
    if (!aIsTop && bIsTop) return 1;

    return countryLocale[a].localeCompare(countryLocale[b]);
  });

  // Find country code that matches current value
  const getSelectedCountry = (val) => {
    return countries.find((country) => `+${getCountryCallingCode(country)}` === val) || "HK";
  };

  // Handle the selection of an option
  const handleSelect = (code) => {
    const newValue = `+${getCountryCallingCode(code)}`;
    setSelectedValue(newValue);
    setIsOpen(false);

    if (onChange) {
      // Create a mock event object that mimics the structure of a native select event
      const mockEvent = {
        target: { value: newValue },
        currentTarget: { value: newValue },
      };
      onChange(mockEvent);
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && event.target instanceof Node && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Update selected value when prop changes
  useEffect(() => {
    setSelectedValue(value);
  }, [value]);

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Selected value display */}
      <button
        type="button"
        className={`flex items-center justify-between ${className}`}
        onClick={() => setIsOpen(!isOpen)}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
      >
        <span className="flex items-center">
          {countryToFlag(getSelectedCountry(selectedValue))}
          <span className="ml-1">{selectedValue}</span>
        </span>
        <span className="ml-1">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className={`h-4 w-4 transition-transform ${isOpen ? "transform rotate-180" : ""}`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </span>
      </button>

      {/* Dropdown options list */}
      {isOpen && (
        <div
          className="absolute z-10 mt-1 w-full bg-white border border-gray-300 rounded-md shadow-lg"
          style={{ maxHeight: maxDropdownHeight }}
        >
          <ul className="py-1 overflow-y-auto max-h-full text-sm" style={{ maxHeight: maxDropdownHeight }} role="listbox">
            {sortedCountries.map((country) => {
              const countryCode = `+${getCountryCallingCode(country)}`;
              const isSelected = selectedValue === countryCode;

              return (
                <li
                  key={country}
                  role="option"
                  aria-selected={isSelected}
                  className={`px-3 py-2 cursor-pointer hover:bg-gray-100 flex items-center 
                    ${isSelected ? "bg-blue-100 font-medium" : ""}`}
                  onClick={() => handleSelect(country)}
                >
                  <span className="mr-2">{countryToFlag(country)}</span>
                  <span>{countryCode}</span>
                  {showCountryName && <span className="ml-2 text-gray-600">{countryLocale[country]}</span>}
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {/* Hidden select for form submission if needed */}
      <select
        name={id}
        id={id}
        value={selectedValue}
        onChange={onChange}
        className="sr-only" // Hidden but still accessible
      >
        {sortedCountries.map((country) => (
          <option key={country} value={`+${getCountryCallingCode(country)}`}>
            {`+${getCountryCallingCode(country)}`}
          </option>
        ))}
      </select>
    </div>
  );
};

export default CountrySelector;
